/* The Founder Console front page, panel by panel. Every figure comes from the API envelope. */
import {
  Users, UserPlus, Crown, Hourglass, User, Activity, MessageSquare, Mic, Moon, Bookmark,
  Heart, BarChart3, Target, ShieldCheck, CheckCircle2, FileText, TrendingUp, TrendingDown,
  Minus, ArrowRight, RotateCcw, Layers, GraduationCap, Undo2, EyeOff,
} from "lucide-react";
import { MetricCard, Panel, Figure, Reason, Delta, Spark, Ring, InfoDot, fmt, Note, DASH } from "./ConsolePrimitives";

const STATE_ICON = { increasing: TrendingUp, improving: TrendingUp, declining: TrendingDown, needs_attention: TrendingDown, stable: Minus, measured: Minus, insufficient: Minus };
const STATE_LABEL = {
  increasing: "Increasing", improving: "Improving", declining: "Declining",
  needs_attention: "Needs attention", stable: "Stable", measured: "Measured",
  insufficient: "Insufficient evidence",
};

export const HeadlineCards = ({ members, catalog, comparison }) => (
  <div className="fc__row fc__row--5" data-testid="fc-headline-cards">
    <MetricCard icon={Users} label="Total Members" env={members.total_members} metric="total_members" catalog={catalog} comparison={comparison} testId="fc-card-total-members" />
    <MetricCard icon={UserPlus} label="New Members" env={members.new_members} metric="new_members" catalog={catalog} comparison={comparison} testId="fc-card-new-members" />
    <MetricCard icon={Crown} label="Premium Members" env={members.premium_members} metric="premium_members" catalog={catalog} comparison={comparison} gold testId="fc-card-premium-members" />
    <MetricCard icon={Hourglass} label="Trial Members" env={members.trial_members} metric="trial_members" catalog={catalog} comparison={comparison} testId="fc-card-trial-members" />
    <MetricCard icon={User} label="Free Members" env={members.free_members} metric="free_members" catalog={catalog} comparison={comparison} testId="fc-card-free-members" />
  </div>
);

export const GrowthPanel = ({ growth, window, catalog }) => {
  const rows = growth || [];
  const maxTotal = Math.max(1, ...rows.map((r) => r.total_members)) * 1.18;
  const w = 600, h = 150, pad = 8;
  const x = (i) => (rows.length < 2 ? pad : pad + (i * (w - pad * 2)) / (rows.length - 1));
  const y = (v) => h - pad - (v / maxTotal) * (h - pad * 2);
  const line = (key) => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(r[key]).toFixed(1)}`).join(" ");
  return (
    <Panel
      title="Member Growth" icon={Activity} testId="fc-growth-panel"
      control={<span className="fc-chip" data-tone="quiet">{window?.label}</span>}
    >
      {rows.length === 0 ? (
        <div className="fc-unavail" data-testid="fc-growth-empty">No member records in this period.</div>
      ) : (
        <>
          <svg viewBox={`0 0 ${w} ${h}`} style={{ width: "100%", height: 160 }} data-testid="fc-growth-chart">
            <defs>
              <linearGradient id="fcGold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(216,184,118,0.34)" />
                <stop offset="100%" stopColor="rgba(216,184,118,0)" />
              </linearGradient>
            </defs>
            <line x1={pad} x2={w - pad} y1={pad} y2={pad} stroke="rgba(168,186,220,0.07)" />
            <line x1={pad} x2={w - pad} y1={h / 2} y2={h / 2} stroke="rgba(168,186,220,0.07)" />
            <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="rgba(168,186,220,0.07)" />
            <path d={`${line("total_members")} L${x(rows.length - 1)},${h - pad} L${x(0)},${h - pad} Z`} fill="url(#fcGold)" />
            <path d={line("total_members")} fill="none" stroke="#d8b876" strokeWidth="1.6" />
            <path d={line("new_members")} fill="none" stroke="#8fa6e8" strokeWidth="1.4" />
          </svg>
          <div style={{ display: "flex", gap: 18, marginTop: 10, fontSize: 12, color: "var(--fc-mute)" }}>
            <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: "#d8b876", marginRight: 6 }} />Total Members</span>
            <span><i style={{ display: "inline-block", width: 8, height: 8, borderRadius: 4, background: "#8fa6e8", marginRight: 6 }} />New Members</span>
            <span style={{ marginLeft: "auto" }}>{rows[0].bucket} → {rows[rows.length - 1].bucket}</span>
          </div>
        </>
      )}
    </Panel>
  );
};

export const DistributionPanel = ({ members, catalog }) => {
  const rows = members.distribution || [];
  const total = members.total_members?.value || 0;
  const colors = { Free: "#d8b876", Trial: "#6fc8c0", Premium: "#8fa6e8" };
  if (members.distribution_current_only) {
    return (
      <Panel title="Member Distribution" icon={Users} testId="fc-distribution-panel">
        <div className="fc-metric__value" data-testid="fc-distribution-total">
          <span className="fc-current-only">Current only</span>
        </div>
        <div className="fc-metric__foot" data-testid="fc-distribution-reason">
          {members.distribution_note}
        </div>
      </Panel>
    );
  }
  let cursor = 0;
  const stops = rows.map((row) => {
    const share = total ? ((row.count || 0) / total) * 100 : 0;
    const stop = `${colors[row.state]} ${cursor}% ${cursor + share}%`;
    cursor += share;
    return stop;
  });
  if (cursor < 100) stops.push(`rgba(168,186,220,0.1) ${cursor}% 100%`);
  return (
    <Panel title="Member Distribution" icon={Users} testId="fc-distribution-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
        <div
          data-testid="fc-distribution-donut"
          style={{
            width: 140, height: 140, flex: "0 0 140px", borderRadius: "50%",
            background: `conic-gradient(${stops.join(", ")})`,
            display: "grid", placeItems: "center", position: "relative",
          }}
        >
          <div style={{
            width: 100, height: 100, borderRadius: "50%", background: "#080d1a",
            display: "grid", placeItems: "center", textAlign: "center",
          }}>
            <div>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: "var(--fc-ivory)" }}
                data-testid="fc-distribution-total">{fmt(total)}</div>
              <div style={{ fontSize: 8.5, letterSpacing: "0.12em", color: "var(--fc-mute)" }}>
                REAL MEMBERS
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: "1 1 160px", minWidth: 0 }}>
          {rows.map((row) => (
            <div className="fc-signal" key={row.state} data-testid={`fc-dist-${row.state.toLowerCase()}`}>
              <i style={{ width: 8, height: 8, borderRadius: 4, background: colors[row.state] }} />
              <span className="fc-signal__label">{row.state}</span>
              <span style={{ color: "var(--fc-ivory)" }}>{fmt(row.count)}</span>
              <span style={{ color: "var(--fc-mute)", width: 52, textAlign: "right" }}>
                {row.share === null ? DASH : `${row.share}%`}
              </span>
            </div>
          ))}
        </div>
      </div>
      <Note testId="fc-distribution-note">
        {members.population?.excluded_total} QA/staff/test identities excluded by declared rule.
      </Note>
    </Panel>
  );
};

export const QuickSignals = ({ signals, catalog, onViewAll }) => (
  <Panel
    title="Quick Signals" icon={Target} testId="fc-quick-signals"
    control={<button className="fc-btn fc-btn--ghost" onClick={onViewAll} data-testid="fc-quick-signals-viewall">View all <ArrowRight style={{ width: 13, height: 13 }} /></button>}
  >
    {(signals || []).map((s) => {
      const Icon = STATE_ICON[s.state] || Minus;
      return (
        <div className="fc-signal" key={s.label} data-testid={`fc-signal-${s.metric}`}>
          <Target className="fc-signal__icon" />
          <span className="fc-signal__label">{s.label}</span>
          <InfoDot metric={s.metric} catalog={catalog} />
          <span className="fc-signal__state" data-state={s.state} data-testid={`fc-signal-state-${s.metric}`}>
            <Icon /> {STATE_LABEL[s.state] || s.state}
          </span>
        </div>
      );
    })}
  </Panel>
);

export const EngagementPanel = ({ health, members, catalog, comparison }) => {
  const cells = [
    { label: "Onboarding Completion", icon: GraduationCap, env: health.onboarding_completion_rate, metric: "onboarding_completion_rate", suffix: "%" },
    { label: "Returning Members", icon: RotateCcw, env: members.returning_member_rate, metric: "returning_member_rate", suffix: "%" },
    { label: "Multi-Surface Adoption", icon: Layers, env: health.multi_surface_rate, metric: "multi_surface_rate", suffix: "%" },
    { label: "Memory Corrections", icon: Undo2, env: health.memory_corrections, metric: "memory_corrections" },
    { label: "Memory Forgets", icon: EyeOff, env: health.memory_forgets, metric: "memory_forgets" },
  ];
  return (
    <Panel title="Engagement & Relationship Signals" icon={Heart} testId="fc-engagement-panel"
      sub="Descriptive evidence. No composite score is computed.">
      <div className="fc-strip fc-strip--5">
        {cells.map((cell) => (
          <div className="fc-strip__cell" key={cell.label} data-testid={`fc-eng-${cell.metric}`}>
            <div className="fc-strip__label">
              <cell.icon />{cell.label}<InfoDot metric={cell.metric} catalog={catalog} />
            </div>
            <Figure env={cell.env} suffix={cell.suffix || ""} className="fc-strip__value" testId={`fc-eng-${cell.metric}-value`} />
            <Delta env={cell.env} comparison={comparison} />
            <Reason env={cell.env} testId={`fc-eng-${cell.metric}-reason`} />
          </div>
        ))}
      </div>
      <Note testId="fc-engagement-neutrality">{health.neutrality}</Note>
    </Panel>
  );
};

export const UsagePanel = ({ usage, catalog, comparison }) => {
  const cells = [
    { label: "Messages", icon: MessageSquare, env: usage.messages_used, metric: "messages_used" },
    { label: "Talk Minutes", icon: Mic, env: usage.talk_minutes_used, metric: "talk_seconds_used" },
    { label: "Dreams Logged", icon: Moon, env: usage.dreams_logged, metric: "dreams_logged" },
    { label: "Saved Talks", icon: Bookmark, env: usage.saved_talks, metric: "saved_talks" },
  ];
  return (
    <Panel title="Usage & Capacity" icon={BarChart3} testId="fc-usage-panel"
      control={<span className="fc-chip" data-tone="quiet">{comparison ? "this period" : ""}</span>}>
      <div className="fc-strip fc-strip--4">
        {cells.map((cell) => (
          <div className="fc-strip__cell" key={cell.label} data-testid={`fc-usage-${cell.metric}`}>
            <div className="fc-strip__label"><cell.icon />{cell.label}<InfoDot metric={cell.metric} catalog={catalog} /></div>
            <Figure env={cell.env} className="fc-strip__value" testId={`fc-usage-${cell.metric}-value`} />
            <Delta env={cell.env} comparison={comparison} />
          </div>
        ))}
      </div>
      <Note testId="fc-usage-guard">{usage.commercial_guard}</Note>
    </Panel>
  );
};

export const SystemPanel = ({ system, catalog }) => (
  <Panel title="System Health" icon={Activity} testId="fc-system-panel"
    control={<span className="fc-chip" data-tone={system.stripe_mode === "test" ? "quiet" : "warn"}>Stripe {system.stripe_mode}</span>}>
    <div className="fc-rings">
      <Ring env={system.api_uptime} label="API Uptime" suffix="%" testId="fc-ring-uptime" />
      <Ring env={system.talk_connect_success_rate} label="Talk Connect Success" suffix="%" tone="var(--fc-gold)" testId="fc-ring-talk" />
      <Ring env={system.capability_avg_latency_ms} label="Avg. Latency" suffix="ms" arc={false} tone="var(--fc-blue)" testId="fc-ring-latency" />
      <Ring env={system.capability_failure_rate} label="Capability Failure Rate" suffix="%" tone="var(--fc-down)" testId="fc-ring-failure" />
    </div>
    <Note testId="fc-system-separation">{system.separation}</Note>
  </Panel>
);

export const RadarPanel = ({ radar, catalog, onViewAll }) => {
  const rows = radar.signals || [];
  const max = Math.max(1, ...rows.map((s) => s.events.value || 0));
  return (
    <Panel title="Top Capability Requests" icon={Target} testId="fc-radar-panel"
      sub="Structured evidence of what members attempt that Arvexis cannot do reliably."
      control={<button className="fc-btn fc-btn--ghost" onClick={onViewAll} data-testid="fc-radar-viewall">View all <ArrowRight style={{ width: 13, height: 13 }} /></button>}>
      {rows.length === 0 ? (
        <div className="fc-unavail" data-testid="fc-radar-empty">
          No capability signal was recorded in this period.
        </div>
      ) : rows.map((s, i) => (
        <div className="fc-cap" key={s.signal_key} data-testid={`fc-radar-row-${i}`}>
          <span className="fc-cap__rank">{i + 1}</span>
          <span className="fc-cap__name">
            <b>{s.category} / {s.operation}</b>
            <span>
              {s.surface} · {s.reason_code || "no reason recorded"} ·{" "}
              {s.evidence.sufficient_evidence ? "evidence sufficient" : "evidence insufficient"}
            </span>
          </span>
          <span className="fc-cap__bar"><i style={{ width: `${((s.events.value || 0) / max) * 100}%` }} /></span>
          <span className="fc-cap__count">{fmt(s.events.value)}</span>
        </div>
      ))}
      <Note testId="fc-radar-coverage">{radar.coverage?.note}</Note>
    </Panel>
  );
};

export const FeedbackPanel = ({ feedback, catalog, comparison }) => (
  <Panel title="Explicit Feedback" icon={MessageSquare} testId="fc-feedback-panel">
    {[
      { label: "Total verdicts", env: feedback.explicit_feedback_total, metric: "explicit_feedback_total" },
      { label: "Confirmed", env: feedback.explicit_feedback_confirmed, metric: "explicit_feedback_confirmed" },
      { label: "Corrected", env: feedback.explicit_feedback_corrected, metric: "explicit_feedback_corrected" },
      { label: "Replies marked helpful", env: feedback.response_feedback_helpful, metric: "response_feedback_helpful" },
      { label: "Replies marked not helpful", env: feedback.response_feedback_not_helpful, metric: "response_feedback_not_helpful" },
    ].map((row) => (
      <div className="fc-signal" key={row.label} data-testid={`fc-feedback-${row.metric}`}>
        <span className="fc-signal__label">{row.label}</span>
        <InfoDot metric={row.metric} catalog={catalog} />
        <span style={{ color: "var(--fc-ivory)" }}>{fmt(row.env?.value)}</span>
      </div>
    ))}
    <Note testId="fc-feedback-mechanism">{feedback.mechanism}</Note>
  </Panel>
);

export const BriefPanel = ({ brief, onReadFull }) => (
  <Panel title="Founder Brief" icon={FileText} testId="fc-brief-panel"
    sub="Key changes, emerging signals and areas for your attention."
    control={<button className="fc-btn" onClick={onReadFull} data-testid="fc-brief-readfull">Read Full Brief <ArrowRight style={{ width: 14, height: 14 }} /></button>}>
    <div className="fc-brief">
      <div>
        <h3 className="fc-brief__h">This period at a glance</h3>
        <p className="fc-brief__p" data-testid="fc-brief-glance">{brief.at_a_glance}</p>
      </div>
      <div>
        <h3 className="fc-brief__h">
          {(brief.attention || []).length} {(brief.attention || []).length === 1 ? "area" : "areas"} for your attention
        </h3>
        {(brief.attention || []).length === 0 ? (
          <p className="fc-brief__p" data-testid="fc-brief-attention-empty">{brief.attention_note}</p>
        ) : (
          <ul className="fc-brief__list">
            {brief.attention.map((a) => (
              <li className="fc-brief__item" key={a.title} data-testid={`fc-brief-attention-${a.rank}`}>
                <span className="fc-brief__num">{a.rank}</span>
                <span>{a.title} — {a.recommendation}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  </Panel>
);

export const PrivacyPanel = ({ privacy }) => (
  <Panel title="Data & Privacy" icon={ShieldCheck} testId="fc-privacy-panel">
    <div className="fc-privacy">
      {[
        ["qa_test_excluded", "QA/Test traffic excluded"],
        ["no_private_content_stored", "No private content stored"],
        ["small_n_protections_active", "Small-N protections active"],
        ["metrics_use_authoritative_sources", "Metrics use authoritative sources"],
        ["deletion_policy_applied", "Deletion policy applied"],
      ].map(([key, label]) => (
        <div className="fc-privacy__row" key={key} data-testid={`fc-privacy-${key}`} title={privacy?.[key]?.detail}>
          <CheckCircle2 />
          <span>
            {label}
            <span style={{ display: "block", color: "var(--fc-mute)", fontSize: 11.5, lineHeight: 1.55, marginTop: 3 }}>
              {privacy?.[key]?.detail}
            </span>
          </span>
        </div>
      ))}
    </div>
  </Panel>
);
